import { generateKeyBetween, zAfter, zBefore } from './fractional';
import type { SurfaceModel } from './SurfaceModel';
import type { GfxElement } from './types';

/**
 * 图层管理（借鉴 AFFiNE LayerManager）：
 *  - DOM 层：需要交互/编辑的元素（便签、文本）
 *  - Canvas 层：批量绘制的轻量图形（形状、连接线、框、分组）
 *  - 用分形索引维护 z 序
 */
export class LayerManager {
  constructor(private surface: SurfaceModel) {}

  /** DOM 渲染层元素（可编辑/图片），按 z 升序。 */
  get domElements(): GfxElement[] {
    return this.surface
      .sorted()
      .filter(el => !el.hidden && (el.type === 'note' || el.type === 'text' || el.type === 'photo'));
  }

  /** Canvas 渲染层元素（图形与装饰），按 z 升序。 */
  get canvasElements(): GfxElement[] {
    const canvasTypes = [
      'shape',
      'connector',
      'rope',
      'frame',
      'group',
      'circle',
      'pin',
      'tape',
      'highlight',
      'redact',
      'brush',
      'stamp',
    ];
    return this.surface.sorted().filter(el => !el.hidden && canvasTypes.includes(el.type));
  }

  /** 最顶层的 z 键。 */
  private topZ(): string | null {
    const sorted = this.surface.sorted();
    return sorted.length ? sorted[sorted.length - 1].z : null;
  }

  private bottomZ(): string | null {
    const sorted = this.surface.sorted();
    return sorted.length ? sorted[0].z : null;
  }

  /** 生成置于顶层的新 z。 */
  nextTopZ(): string {
    return zAfter(this.topZ());
  }

  /** 生成置于底层的新 z。 */
  nextBottomZ(): string {
    return zBefore(this.bottomZ());
  }

  /** 生成两个元素之间的 z。 */
  zBetween(below: GfxElement | undefined, above: GfxElement | undefined): string {
    return generateKeyBetween(below?.z ?? null, above?.z ?? null);
  }

  bringToFront(id: string): void {
    this.surface.update(id, { z: this.nextTopZ() });
  }

  sendToBack(id: string): void {
    this.surface.update(id, { z: this.nextBottomZ() });
  }

  /** 相对移动一位。 */
  moveZ(id: string, direction: 'forward' | 'backward'): void {
    const sorted = this.surface.sorted().filter(el => el.id !== id);
    const current = this.surface.get(id);
    if (!current) return;
    const index = sorted.findIndex(el => el.z > current.z);
    if (direction === 'forward') {
      if (index === -1) {
        this.bringToFront(id);
        return;
      }
      const above = sorted[index + 1];
      const below = sorted[index];
      const z = this.zBetween(below, above);
      this.surface.update(id, { z });
    } else {
      if (index <= 0) {
        this.sendToBack(id);
        return;
      }
      const above = sorted[index];
      const below = sorted[index - 1];
      const z = this.zBetween(below, above);
      this.surface.update(id, { z });
    }
  }

  /**
   * 归一化 z 序（当分形索引退化时兜底）：
   * 按当前顺序重新生成均匀间隔的键。
   */
  normalize(): void {
    const sorted = this.surface.sorted();
    let prev: string | null = null;
    for (let i = 0; i < sorted.length; i++) {
      const next = i + 1 < sorted.length ? sorted[i + 1].z : null;
      const z = generateKeyBetween(prev, next);
      if (z !== sorted[i].z) this.surface.update(sorted[i].id, { z });
      prev = z;
    }
  }
}